import React, { useEffect, useState } from 'react';
import { useAuth, getApiUrl } from '../hooks/useAuth';
import { formatDateToDDMMYYYY } from '../components/FormatedDate';
import { ToastContainer, toast } from 'react-toastify';

const getRoleBadgeClass = (role) => {
     switch (role) {
          case 'SUPER_ADMIN': return 'badge-danger';
          case 'ADMIN': return 'badge-warning';
          case 'TECHNICIAN': return 'badge-info';
          default: return 'badge-secondary';
     }
};

const getStatusBadgeClass = (status) => (status === 'Active' ? 'badge-success' : 'badge-secondary');

const initialsFromName = (name) => {
     if (!name || typeof name !== 'string') return '';
     const parts = name.trim().split(/\s+/);
     const a = parts[0]?.[0] || '';
     const b = parts[1]?.[0] || '';
     return (a + b).toUpperCase() || a.toUpperCase();
};


export default function Technicians() {
     const { user } = useAuth();
     const [list, setList] = useState([]);
     const [loading, setLoading] = useState(false);
     const [fetchError, setFetchError] = useState('');
     const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
     const [branchList, setBranchList] = useState([]);

     const [filter, setFilter] = useState('');
     const [currentPage, setCurrentPage] = useState(1);
     const pageSize = 10;


     const [showCreate, setShowCreate] = useState(false);
     const [saving, setSaving] = useState(false);
     const [formError, setFormError] = useState('');
     const [form, setForm] = useState({
          emp_code: '',
          name: '',
          pms_id: '',
          branch_id: '',
          status: 'A', // A = Active, I = Inactive
     });

     const [editTech, setEditTech] = useState(null);
     const [editForm, setEditForm] = useState({
          emp_code: '',
          name: '',
          pms_id: '',
          branch_id: '',
          status: 'A',
     });
     const [editError, setEditError] = useState('');
     const [editSaving, setEditSaving] = useState(false);

     // Build the full URL using the shared helper
     const LIST_URL = getApiUrl('/getTechnicians');
     const CREATE_URL = getApiUrl('/createTechnician'); // adjust if your endpoint differs
     const UPDATE_URL = getApiUrl('/updateTechnician/' + editTech?.id); // adjust if your endpoint differs

     // Fetch branches for the dropdown
     const fetchBranches = async () => {
          try {
               const res = await fetch(getApiUrl('/getBranches'), {
                    method: 'GET',
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
               });
               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const body = isJson ? await res.json().catch(() => ({})) : {};
               if (!res.ok) throw new Error(body?.message || `Failed to fetch branches (${res.status})`);
               setBranchList(body.data || []);
          } catch (e) {
               setFetchError(e.message || 'Failed to fetch branches');
          }
     };
     useEffect(() => {
          let alive = true;
          (async () => {
               if (!alive) return;
               await fetchBranches();
          })();
          return () => {
               alive = false;
          };
     }, [token]);
     // Factor fetch into a function so we can reuse after create
     const fetchTechnicians = async () => {
          try {
               setLoading(true);
               setFetchError('');
               const res = await fetch(LIST_URL, {
                    method: 'GET',
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
               });
               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const body = isJson ? await res.json().catch(() => ({})) : {};
               if (!res.ok) throw new Error(body?.message || `Failed to fetch technicians (${res.status})`);

               const items = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
               const rows = items.map((r) => {
                    const code = r.emp_code || r.user_code || '';
                    const name = r.name || r.user_name || '';
                    const role = r.role_name || r.role || 'TECHNICIAN';
                    const status = r.status === 'A' ? 'Active' : r.status === 'I' ? 'Inactive' : r.status || '-';
                    const branch = r.branch_name ?? r.branch ?? r.branch_id ?? '-';
                    const branch_id = r.branch_id || '';
                    const pms_id = r.pms_id || '-';
                    const created_at = r.created_at || '';
                    const email = r.email || (code ? `${code}@vdc.com` : '-');
                    return {
                         id: r.techni_id || r.tech_id || r.user_id || r.id || code || `${name}-${role}`,
                         code,
                         name,
                         role,
                         status,
                         branch,
                         created_at,
                         email,
                         pms_id,
                         branch_id,
                    };
               });
               setList(rows);
          } catch (e) {
               console.error('Error fetching technicians:', e);
          } finally {
               setLoading(false);
          }
     };

     useEffect(() => {
          let alive = true;
          (async () => {
               if (!alive) return;
               await fetchTechnicians();
          })();
          return () => {
               alive = false;
          };
     }, [LIST_URL, token]);

     const total = list.length;
     const activeCount = list.filter((t) => t.status === 'Active').length;

     // NEW: helpers for modal form
     const resetForm = () =>
          setForm({ emp_code: '', name: '', pms_id: '', branch_id: '', status: 'A' });

     const validateForm = () => {
          if (!form.emp_code?.trim()) return 'Employee code is required';
          if (!form.name?.trim()) return 'Name is required';
          if (!String(form.branch_id || '').trim()) return 'Branch is required';
          if (!form.status) return 'Status is required';
          return '';
     };

     const handleSave = async () => {
          setFormError('');
          const validationMsg = validateForm();
          if (validationMsg) {
               setFormError(validationMsg);
               return;
          }

          try {
               setSaving(true);

               // Send URL-encoded body; backend supports getVar/getJSON
               const body = new URLSearchParams();
               body.append('emp_code', form.emp_code.trim());
               body.append('name', form.name.trim());
               body.append('branch_id', String(form.branch_id).trim());
               body.append('status', form.status); // 'A' or 'I'
               if (form.pms_id?.trim()) body.append('pms_id', form.pms_id.trim());

               const res = await fetch(CREATE_URL, {
                    method: 'POST',
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body,
               });

               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const data = isJson ? await res.json().catch(() => ({})) : {};

               if (!res.ok || data?.status !== true) {
                    const serverMsg =
                         data?.message ||
                         (res.status === 409 ? 'Technician already exists' : '') ||
                         `Failed to create technician (${res.status})`;
                    throw new Error(serverMsg);
               }
               toast.success('Technician created successfully');

               await fetchTechnicians();
               resetForm();
               setShowCreate(false);
          } catch (e) {
               console.error('Error creating technician:', e);
          } finally {
               setSaving(false);
          }
     };

     const filteredList = list.filter(
          (t) =>
               t.name.toLowerCase().includes(filter.toLowerCase()) ||
               t.code.toLowerCase().includes(filter.toLowerCase()) ||
               t.pms_id.toLowerCase().includes(filter.toLowerCase()) ||
               t.branch.toLowerCase().includes(filter.toLowerCase())
     );

     const totalPages = Math.ceil(filteredList.length / pageSize);
     const paginatedList = filteredList.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize
     );

     return (
          <div style={{ padding: '2rem' }}>
               <ToastContainer position="top-right" />
               <div className="mb-3 d-flex justify-content-between align-items-center">
                    <h1 className="h3 mb-0 text-gray-800">Technicians</h1>
                    <input
                         type="text"
                         className="form-control"
                         style={{ maxWidth: 300 }}
                         placeholder="Search"
                         value={filter}
                         onChange={e => {
                              setFilter(e.target.value);
                              setCurrentPage(1);
                         }}
                    />

                    {/* NEW: Add Technician button */}
                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                         <i className="fas fa-user-nurse mr-2" /> Add Technician
                    </button>
               </div>

               {fetchError && (
                    <div className="alert alert-danger">
                         <i className="fas fa-exclamation-triangle mr-2" />
                         {fetchError}
                    </div>
               )}

               {loading && (
                    <div className="alert alert-info">
                         <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                         Loading technicians...
                    </div>
               )}







               <div className="card shadow mb-4">
                    <div className="card-header py-3">
                         <h6 className="m-0 font-weight-bold text-primary">All Technicians</h6>
                    </div>
                    <div className="card-body">
                         <div className="table-responsive">
                              <table className="table table-bordered" width="100%" cellSpacing="0">
                                   <thead>
                                        <tr>
                                             <th>Tech Code</th>
                                             <th>Name</th>
                                             <th>PMS ID</th>
                                             <th>Role</th>
                                             <th>Status</th>
                                             <th>Branch</th>
                                             <th>Created At</th>
                                             <th></th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {paginatedList.length === 0 ? (
                                             <tr>
                                                  <td colSpan="7" className="text-center p-4">No technicians found</td>
                                             </tr>
                                        ) : (
                                             paginatedList.map((t) => {
                                                  // Defensive mapping for all fields
                                                  const status =
                                                       t.status === 'A'
                                                            ? 'Active'
                                                            : t.status === 'I'
                                                                 ? 'Inactive'
                                                                 : t.status === '0'
                                                                      ? 'Inactive'
                                                                      : t.status === '1'
                                                                           ? 'Active'
                                                                           : t.status || '-';
                                                  return (
                                                       <tr key={t.techni_id || t.id}>
                                                            <td>{t.emp_code || t.code || '-'}</td>
                                                            <td>
                                                                 <div className="d-flex align-items-center">

                                                                      <div>{t.name || '-'}</div>
                                                                 </div>
                                                            </td>
                                                            <td>{t.pms_id || '-'}</td>
                                                            <td>
                                                                 <span className={`badge ${getRoleBadgeClass(t.role_name || t.role)}`}>
                                                                      {t.role_name || t.role || '-'}
                                                                 </span>
                                                            </td>
                                                            <td>
                                                                 <span className={`badge ${getStatusBadgeClass(status)}`}>
                                                                      {status}
                                                                 </span>
                                                            </td>
                                                            <td>{t.branch_name || t.branch || t.branch_id || '-'}</td>
                                                            <td>
                                                                 {formatDateToDDMMYYYY(t.created_at)}
                                                            </td>
                                                            <td><button
                                                                 className="btn btn-sm btn-outline-primary"
                                                                 onClick={() => {
                                                                      setEditTech(t);
                                                                      setEditForm({
                                                                           emp_code: t.emp_code || t.code || '',
                                                                           name: t.name || '',
                                                                           pms_id: t.pms_id || '',
                                                                           branch_id: t.branch_id || '',
                                                                           status: t.status === 'Active' || t.status === 'A' || t.status === '1' ? 'A' : 'I',
                                                                      });
                                                                      setEditError('');
                                                                 }}
                                                            >
                                                                 <i className="fas fa-edit"></i>{t.pms_id ? ' Edit' : ''}
                                                            </button></td>
                                                       </tr>
                                                  );
                                             })
                                        )}

                                   </tbody>
                              </table>
                              {/* Pagination controls */}
                              <nav>
                                   <ul className="pagination mb-0">
                                        <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                                             <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                                                  &laquo;
                                             </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, idx) => (
                                             <li key={idx + 1} className={`page-item${currentPage === idx + 1 ? ' active' : ''}`}>
                                                  <button className="page-link" onClick={() => setCurrentPage(idx + 1)}>
                                                       {idx + 1}
                                                  </button>
                                             </li>
                                        ))}
                                        <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                                             <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                                                  &raquo;
                                             </button>
                                        </li>
                                   </ul>
                              </nav>
                         </div>
                    </div>
               </div>

               {/* NEW: Create Technician Modal */}
               {showCreate && (
                    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                         <div className="modal-dialog modal-lg">
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Add New Technician</h5>
                                        <button
                                             type="button"
                                             className="close"
                                             onClick={() => {
                                                  setShowCreate(false);
                                                  resetForm();
                                                  setFormError('');
                                             }}
                                        >
                                             <span>&times;</span>
                                        </button>
                                   </div>

                                   <div className="modal-body">
                                        {formError && (
                                             <div className="alert alert-danger py-2">{formError}</div>
                                        )}

                                        <form onSubmit={(e) => e.preventDefault()}>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Employee Code</label><span className="text-danger">*</span>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={form.emp_code}
                                                                 onChange={(e) => setForm((f) => ({ ...f, emp_code: e.target.value }))}
                                                                 required
                                                            />
                                                       </div>
                                                  </div>

                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Full Name</label><span className="text-danger">*</span>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={form.name}
                                                                 onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                                                 required
                                                            />
                                                       </div>
                                                  </div>
                                             </div>

                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>PMS ID </label><span className="text-danger">*</span>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={form.pms_id}
                                                                 onChange={(e) => setForm((f) => ({ ...f, pms_id: e.target.value }))}
                                                            />
                                                       </div>
                                                  </div>

                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Branch</label><span className="text-danger">*</span>
                                                            <select
                                                                 className="form-control"
                                                                 value={form.branch_id}
                                                                 onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value }))}
                                                            >
                                                                 <option value="">Select branch</option>
                                                                 {branchList.map((b) => (
                                                                      <option key={b.branch_id || b.id} value={b.branch_id || b.id}>
                                                                           {b.branch_name || b.name || b.branch_id || b.id}
                                                                      </option>
                                                                 ))}
                                                            </select>
                                                       </div>
                                                  </div>
                                             </div>
                                        </form>
                                   </div>

                                   <div className="modal-footer">
                                        <button
                                             type="button"
                                             className="btn btn-secondary"
                                             onClick={() => {
                                                  setShowCreate(false);
                                                  resetForm();
                                                  setFormError('');
                                             }}
                                             disabled={saving}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             type="button"
                                             className="btn btn-primary"
                                             onClick={handleSave}
                                             disabled={saving}
                                        >
                                             {saving ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                                       Saving...
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-save mr-2" />
                                                       Add Technician
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}


               {editTech && (
                    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                         <div className="modal-dialog modal-lg">
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Update Technician</h5>
                                        <button
                                             type="button"
                                             className="close"
                                             onClick={() => {
                                                  setEditTech(null);
                                                  setEditError('');
                                             }}
                                        >
                                             <span>&times;</span>
                                        </button>
                                   </div>
                                   <div className="modal-body">
                                        {editError && (
                                             <div className="alert alert-danger py-2">{editError}</div>
                                        )}
                                        <form onSubmit={e => e.preventDefault()}>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Employee Code</label>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={editForm.emp_code}
                                                                 onChange={e => setEditForm(f => ({ ...f, emp_code: e.target.value }))}
                                                                 required
                                                                 disabled // usually emp_code is not editable
                                                            />
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Full Name</label>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={editForm.name}
                                                                 onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                                                 required
                                                            />
                                                       </div>
                                                  </div>
                                             </div>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>PMS ID</label>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={editForm.pms_id}
                                                                 onChange={e => setEditForm(f => ({ ...f, pms_id: e.target.value }))}
                                                            />
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Branch  </label>

                                                            <select
                                                                 className="form-control"
                                                                 value={String(editForm.branch_id ?? '').trim()}
                                                                 onChange={e => setEditForm(f => ({ ...f, branch_id: e.target.value }))}
                                                            >
                                                                 <option value="">Select branch</option>
                                                                 {branchList.map((b) => (
                                                                      <option
                                                                           key={String(b.branch_id).trim()}
                                                                           value={String(b.branch_id).trim()}
                                                                      >
                                                                           {b.branch_name}
                                                                      </option>
                                                                 ))}
                                                            </select>

                                                       </div>
                                                  </div>
                                             </div>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Status</label>
                                                            <select
                                                                 className="form-control"
                                                                 value={editForm.status}
                                                                 onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                                                            >
                                                                 <option value="A">Active</option>
                                                                 <option value="I">Inactive</option>
                                                            </select>
                                                       </div>
                                                  </div>
                                             </div>
                                        </form>
                                   </div>
                                   <div className="modal-footer">
                                        <button
                                             type="button"
                                             className="btn btn-secondary"
                                             onClick={() => {
                                                  setEditTech(null);
                                                  setEditError('');
                                             }}
                                             disabled={editSaving}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             type="button"
                                             className="btn btn-primary"
                                             onClick={async () => {
                                                  setEditError('');
                                                  // Validation
                                                  if (!editForm.emp_code.trim()) return setEditError('Employee code is required');
                                                  if (!editForm.name.trim()) return setEditError('Name is required');
                                                  if (!String(editForm.branch_id || '').trim()) return setEditError('Branch is required');
                                                  if (!editForm.status) return setEditError('Status is required');
                                                  try {
                                                       setEditSaving(true);
                                                       const body = new URLSearchParams();
                                                       body.append('techni_id', editTech.techni_id || editTech.id || '');
                                                       body.append('emp_code', editForm.emp_code.trim());
                                                       body.append('name', editForm.name.trim());
                                                       body.append('branch_id', String(editForm.branch_id).trim());
                                                       body.append('status', editForm.status);
                                                       if (editForm.pms_id?.trim()) body.append('pms_id', editForm.pms_id.trim());
                                                       const res = await fetch(UPDATE_URL, {
                                                            method: 'POST',
                                                            headers: {
                                                                 Accept: 'application/json',
                                                                 ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                                            },
                                                            body,
                                                       });
                                                       const isJson = (res.headers.get('content-type') || '').includes('application/json');
                                                       const data = isJson ? await res.json().catch(() => ({})) : {};
                                                       if (!res.ok || data?.status !== true) {
                                                            throw new Error(data?.message || `Failed to update technician (${res.status})`);
                                                       }
                                                       await fetchTechnicians();
                                                       setEditTech(null);
                                                       toast.success('Technician updated successfully');
                                                  } catch (e) {
                                                       console.error('Error updating technician:', e);
                                                  } finally {
                                                       setEditSaving(false);
                                                  }
                                             }}
                                             disabled={editSaving}
                                        >
                                             {editSaving ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                                       Saving...
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-save mr-2" />
                                                       Update Technician
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}
          </div>
     );
}